<script lang="ts" setup>
import type { ResponsiveImageType } from "vue-datocms";
import type { ProductGroupPromoRecord } from "~~/server/graphql/datocms/generated";
import type { CreativeEventData } from "~/composables/useGtmTracking";
import { Image as DatocmsImage } from "vue-datocms";

const { data, categoryName, categoryCode } = defineProps<{
  data: ProductGroupPromoRecord;
  categoryName: string;
  categoryCode: string;
}>();

const localePath = useLocalePath();
const link = computed(() => getLinkForType(data.linkItem, localePath));

const creativeName = `Productgroup Promo - ${categoryName}`;
const creativeData: CreativeEventData = {
  creativeTitle: data.title,
  creativeFileTitle: data.productImage.title,
  creativeFileName: data.productImage.filename,
  creativeHyperlink: link.value.to,
  creativeCategoryCode: categoryCode,
};

const { trackCreativeDisplayEvent, trackCreativeClickEvent } = useGtmTracking();
onMounted(() => {
  trackCreativeDisplayEvent(
    creativeName,
    creativeData,
  );
});
</script>

<template>
  <div class="promo-container h-full overflow-hidden rounded-lg">
    <div class="promo-container__bg relative">
      <DatocmsImage
        :data="data.backgroundImage.responsiveImage as ResponsiveImageType"
        :lazy-load="false" object-fit="cover" object-position="right" class="size-full rounded-lg"
      />
      <Vector class="vector--background -bottom-5xl lg:-left-5xl absolute left-[-10%] h-auto w-[200px] lg:-bottom-1/4 lg:w-[270px] xl:w-[340px]" />
    </div>

    <div class="promo-container__img z-1">
      <div class="flex h-full items-center justify-center">
        <DatocmsImage
          :data="data.productImage.responsiveImage as ResponsiveImageType"
          :lazy-load="true"
        />
      </div>
    </div>

    <div class="promo-container__content z-2">
      <div class="p-lg flex h-full flex-col">
        <div :class="{ 'text-white': data.textColorMode, 'text-black': !data.textColorMode }">
          <h2>{{ data.title }}</h2>
          <span class="text-[22px] lg:text-[24px]" v-html="data.text" />
        </div>

        <div class="mt-auto text-end">
          <UButton
            variant="solid" color="neutral" size="md" :to="link.to ?? '/'"
            @click="trackCreativeClickEvent(creativeName, creativeData)"
          >
            {{ link.label }}
          </UButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.promo-container {
  display: grid;
  grid-template-areas: "stack";

  .promo-container__bg {
    grid-area: stack;
  }

  .promo-container__img {
    grid-area: stack;
  }

  .promo-container__content {
    grid-area: stack;
  }
}

.vector--background {
  --esc-vector-fill-color: var(--color-neutral-100);
  --esc-vector-mix-blend-mode: soft-light;
  --esc-vector-opacity: 0.3;
}
</style>
